import React, { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2,
  Circle,
  Trash2,
  Clock,
  User,
  Layers,
  GripVertical,
} from "lucide-react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { updateTask } from "../services/taskService";

/* ===================================================== */
/* TASK ITEM                                             */
/* ===================================================== */

export default function TaskItem({
  task,
  users = [],
  phases = [],
  onToggle,
  onDelete,
  onLocalUpdate,
}) {
  /* ===================================================== */
  /* DRAG                                                  */
  /* ===================================================== */

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
  } = useSortable({ id: task._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isCompleted = task.status === "completed";

  /* ===================================================== */
  /* STATE                                                 */
  /* ===================================================== */

  const [state, setState] = useState({
    title: task.title || "",
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : "",
    assignedTo:
      typeof task.assignedTo === "object"
        ? task.assignedTo?._id || ""
        : task.assignedTo || "",
    phaseId:
      typeof task.phaseId === "object"
        ? task.phaseId?._id || ""
        : task.phaseId || "",
  });

  const [editingTitle, setEditingTitle] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /* ===================================================== */
  /* SYNC WITH PROPS                                       */
  /* ===================================================== */

  useEffect(() => {
    setState({
      title: task.title || "",
      dueDate: task.dueDate ? task.dueDate.slice(0, 10) : "",
      assignedTo:
        typeof task.assignedTo === "object"
          ? task.assignedTo?._id || ""
          : task.assignedTo || "",
      phaseId:
        typeof task.phaseId === "object"
          ? task.phaseId?._id || ""
          : task.phaseId || "",
    });
  }, [task]);

  /* ===================================================== */
  /* SAFE PATCH SAVE                                       */
  /* ===================================================== */

  const savePatch = useCallback(
    async (patch) => {
      try {
        setSaving(true);
        setError(null);

        if (patch.title !== undefined && !patch.title.trim()) {
          setError("Task title cannot be empty");
          return;
        }

        const updated = await updateTask(task._id, patch);
        onLocalUpdate?.(updated);
      } catch (err) {
        setError(err.message || "Update failed");
      } finally {
        setSaving(false);
      }
    },
    [task._id, onLocalUpdate]
  );

  /* ===================================================== */
  /* FIELD UPDATE                                          */
  /* ===================================================== */

  const updateField = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));

    savePatch({
      [key]: value === "" ? null : value,
    });
  };

  /* ===================================================== */
  /* TITLE HANDLERS                                        */
  /* ===================================================== */

  const handleTitleSave = () => {
    const newTitle = state.title.trim();

    setEditingTitle(false);

    if (!newTitle) {
      setState((p) => ({ ...p, title: task.title }));
      setError("Task title cannot be empty");
      return;
    }

    if (newTitle !== task.title) {
      savePatch({ title: newTitle });
    }
  };

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`group flex items-start gap-3 px-4 py-3 bg-white transition
        ${saving ? "opacity-70" : "hover:bg-gray-50"}
      `}
    >
      {/* DRAG HANDLE */}
      <div
        {...attributes}
        {...listeners}
        className="mt-1 cursor-grab text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-4 h-4" />
      </div>

      {/* STATUS */}
      <button
        onClick={() => onToggle(task)}
        disabled={saving}
        className="mt-1 text-gray-400 hover:text-indigo-600"
      >
        {isCompleted ? (
          <CheckCircle2 className="w-5 h-5 text-indigo-600" />
        ) : (
          <Circle className="w-5 h-5" />
        )}
      </button>

      {/* CONTENT */}
      <div className="flex-1 min-w-0">

        {/* TITLE */}
        {editingTitle ? (
          <input
            value={state.title}
            onChange={(e) =>
              setState((p) => ({ ...p, title: e.target.value }))
            }
            onBlur={handleTitleSave}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleTitleSave();
              if (e.key === "Escape") {
                setState((p) => ({ ...p, title: task.title }));
                setEditingTitle(false);
              }
            }}
            autoFocus
            className="w-full text-sm border rounded px-2 py-1"
          />
        ) : (
          <div
            onClick={() => setEditingTitle(true)}
            className={`cursor-text text-sm ${
              isCompleted
                ? "line-through text-gray-400"
                : "text-gray-800"
            }`}
          >
            {state.title || "Untitled task"}
          </div>
        )}

        {/* META */}
        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-500">

          {/* DATE */}
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <input
              type="date"
              value={state.dueDate}
              onChange={(e) =>
                updateField("dueDate", e.target.value)
              }
              className="bg-transparent focus:outline-none"
            />
          </span>

          {/* ASSIGNEE */}
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <select
              value={state.assignedTo}
              onChange={(e) =>
                updateField("assignedTo", e.target.value)
              }
              className="bg-transparent focus:outline-none"
            >
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name}
                </option>
              ))}
            </select>
          </span>

          {/* PHASE */}
          {phases.length > 0 && (
            <span className="flex items-center gap-1">
              <Layers className="w-3 h-3" />
              <select
                value={state.phaseId}
                onChange={(e) =>
                  updateField("phaseId", e.target.value)
                }
                className="bg-transparent focus:outline-none"
              >
                <option value="">No phase</option>
                {phases.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </span>
          )}
        </div>

        {/* ERROR */}
        {error && (
          <div className="text-xs text-red-500 mt-1">
            {error}
          </div>
        )}
      </div>

      {/* DELETE */}
      <button
        onClick={onDelete}
        disabled={saving}
        className="opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-red-600"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}